import { AxiosResponse } from 'axios';

import { HttpClient } from 'api/HttpClient';
import { Address } from 'api/ApplicationApi';
import { ErrorDto } from 'errors/ErrorDto';

export interface BorrowerShortInfo {
  id: string;
  locked: boolean;
  accountEmail: string | null;
  isEmailVerified: boolean;
}

export interface BorrowerDto {
  id: string;
  locked: boolean;
  lockReason: BorrowerLockReason | null;
  variables: BorrowerVariablesDto;
}

export enum BorrowerLockReason {
  Fraud = 'FRAUD',
  BankFraud = 'Bank Fraud',
}

interface BorrowerVariablesDto {
  borrower_first_name: string;
  borrower_last_name: string;
  borrower_personal_id_number: string;
  borrower_date_of_birth: string;
  borrower_email: string;
  borrower_phone: string;
  borrower_home_address: Address;
  borrower_is_permanent_resident: boolean;
  borrower_fraud: boolean | null;
  borrower_county: string | null;
}

export interface BorrowerAddress {
  streetName: string;
  streetNumber: string;
  city: string;
  stateOrProvince: string;
  zipOrPostalCode: string;
  county?: string | null;
}

export interface CreateBorrowerDto {
  firstName: string;
  lastName: string;
  personalIdNumber: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  address: BorrowerAddress;
}

export interface UpdateBorrowerDto {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
  phone?: string;
  address?: BorrowerAddress;
}

export default class BorrowerApi extends HttpClient {
  public constructor() {
    super(process.env.REACT_APP_API_URL + '/borrowers' || '');
  }

  public async getBorrowerShortInfo({
    ssn,
    email,
  }: {
    ssn?: string;
    email?: string;
  }): Promise<AxiosResponse<BorrowerShortInfo>> {
    const params = new URLSearchParams();

    if (ssn) {
      params.append('ssn', ssn);
    }

    if (email) {
      params.append('email', email);
    }

    return this.instance
      .get(`/getShortInfo`, { params: params })
      .then((response: AxiosResponse<BorrowerShortInfo>) => response.data);
  }

  public async getBorrowerByToken() {
    return this.instance.get('').then((response: AxiosResponse<BorrowerDto>) => response.data);
  }

  public async getBorrowerByID(id: string) {
    return this.instance.get(`?borrowerId=${id}`).then((response: AxiosResponse<BorrowerDto>) => response.data);
  }

  public async createBorrower(createBorrower: CreateBorrowerDto) {
    return this.instance
      .post('', createBorrower)
      .then((response: AxiosResponse<BorrowerDto | ErrorDto>) => {
        return response.data;
      })
      .catch((error) => {
        throw error;
      });
  }

  public async updateBorrower(updateBorrowerDto: UpdateBorrowerDto): Promise<BorrowerDto> {
    return this.instance.put('', updateBorrowerDto).then((response: AxiosResponse<BorrowerDto>) => response.data);
  }
}

export const borrowerApi = new BorrowerApi();
